import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  GetNotificationSettings200,
  GetNotificationSettingsParams,
  UpdateNotificationSettings200,
  UpdateNotificationSettingsBody,
  UpdateNotificationSettingsParams
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * @summary Get notification settings
 */
export const getNotificationSettings = (
    params: GetNotificationSettingsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetNotificationSettings200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/notifications/settings`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetNotificationSettingsQueryKey = (params: GetNotificationSettingsParams,) => {
    return [`https://api.ugot.uk/api/notifications/settings`, ...(params ? [params]: [])] as const;
    }

    
export const getGetNotificationSettingsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getNotificationSettings>>, TError = AxiosError<void>>(params: GetNotificationSettingsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getNotificationSettings>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNotificationSettingsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotificationSettings>>> = ({ signal }) => getNotificationSettings(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getNotificationSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNotificationSettingsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getNotificationSettings>>>
export type GetNotificationSettingsInfiniteQueryError = AxiosError<void>

/**
 * @summary Get notification settings
 */
export const useGetNotificationSettingsInfinite = <TData = Awaited<ReturnType<typeof getNotificationSettings>>, TError = AxiosError<void>>(
 params: GetNotificationSettingsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getNotificationSettings>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetNotificationSettingsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetNotificationSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getNotificationSettings>>, TError = AxiosError<void>>(params: GetNotificationSettingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNotificationSettings>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNotificationSettingsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotificationSettings>>> = ({ signal }) => getNotificationSettings(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNotificationSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNotificationSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getNotificationSettings>>>
export type GetNotificationSettingsQueryError = AxiosError<void>

/**
 * @summary Get notification settings
 */
export const useGetNotificationSettings = <TData = Awaited<ReturnType<typeof getNotificationSettings>>, TError = AxiosError<void>>(
 params: GetNotificationSettingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNotificationSettings>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetNotificationSettingsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * @summary Update notification settings
 */
export const updateNotificationSettings = (
    updateNotificationSettingsBody: UpdateNotificationSettingsBody,
    params: UpdateNotificationSettingsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UpdateNotificationSettings200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/notifications/settings`,
      updateNotificationSettingsBody,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }



export const getUpdateNotificationSettingsMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNotificationSettings>>, TError,{data: UpdateNotificationSettingsBody;params: UpdateNotificationSettingsParams}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof updateNotificationSettings>>, TError,{data: UpdateNotificationSettingsBody;params: UpdateNotificationSettingsParams}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNotificationSettings>>, {data: UpdateNotificationSettingsBody;params: UpdateNotificationSettingsParams}> = (props) => {
          const {data,params} = props ?? {};

          return  updateNotificationSettings(data,params,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateNotificationSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateNotificationSettings>>>
    export type UpdateNotificationSettingsMutationBody = UpdateNotificationSettingsBody
    export type UpdateNotificationSettingsMutationError = AxiosError<void>

    /**
 * @summary Update notification settings
 */
export const useUpdateNotificationSettings = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNotificationSettings>>, TError,{data: UpdateNotificationSettingsBody;params: UpdateNotificationSettingsParams}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof updateNotificationSettings>>,
        TError,
        {data: UpdateNotificationSettingsBody;params: UpdateNotificationSettingsParams},
        TContext
      > => {

      const mutationOptions = getUpdateNotificationSettingsMutationOptions(options);

      return useMutation(mutationOptions);
    }
    